import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { FC, memo } from 'react';

import { BasketItemActionBar } from '@/components/modules/basket-page/basket-item-product/basket-item-action-bar';
import { BasketItemActionBarMini } from '@/components/modules/basket-page/basket-item-product/basket-item-action-bar-mini';
import { BlockCalc, InformationOverpaymentCalculation, BlockArentaInfo } from '@/components/modules/caclulator';
import { TVariantsCreditProducts } from '@/components/modules/caclulator/types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { addBasketSubmitProduct } from '@/store/reducers/basket/slice';

import { TRentProduct } from '@/entities/product';

import { BASKET_SUBMIT_URL, LOGIN_URL, PRODUCT_URL, REGISTRATION_URL } from '@/shared/constants/paths';
import { DEFAULT_IMAGE_SRC } from '@/shared/constants/settings';
import { useTranslate } from '@/shared/hooks/use-translate';
import { Button } from '@/shared/ui/button';
import { DangerIcon, DoubleArrow2Icon, SuccessIcon } from '@/shared/ui/icons';

import * as Styled from './styled';
import { WithPopover } from './with-popover';

type TProps = {
    product: TRentProduct;
    variantsCreditProducts: TVariantsCreditProducts;
};

export const BasketItemProduct: FC<TProps> = memo(({ product, variantsCreditProducts }) => {
    const router = useRouter();

    const dispatch = useAppDispatch();

    const { t } = useTranslate();

    const { cityUser, auth, data, isNewQuestionnaireAllowed } = useAppSelector((state) => state.user);

    const isActive = product?.status === 'active';

    const handleProcessingProduct = async () => {
        if (!auth.authenticated) {
            await router.push('sign-in');

            return;
        }

        if (!isActive || !isNewQuestionnaireAllowed?.answer) return;

        dispatch(addBasketSubmitProduct({ product }));

        const routeUser = auth.isUserLead ? REGISTRATION_URL : `${BASKET_SUBMIT_URL}?id=${product.id}`;

        router.push(data ? routeUser : LOGIN_URL);
    };

    return (
        <Styled.Wrapper>
            <div className="img-block">
                <div className="img">
                    <Image src={product?.photos?.[0]?.image ?? DEFAULT_IMAGE_SRC} alt={product.name} layout="fill" />
                </div>
                <BasketItemActionBarMini productItem={product} />
            </div>
            <div className="card-block">
                <div className="title">
                    <Link href={`${PRODUCT_URL}/${product.id}`}>{product.name}</Link>
                </div>
                <div className="city">
                    <div>
                        {product?.storageCity === cityUser.name ? (
                            <div className="inf-delivery">
                                <div>
                                    <SuccessIcon width={13} />
                                </div>
                                <div>{t('Товар находится в вашем городе')}</div>
                            </div>
                        ) : (
                            <div className="inf-delivery">
                                <div>
                                    <DangerIcon width={13} />
                                </div>
                                <div>{t('Товар находится в другом городе')}</div>
                            </div>
                        )}
                    </div>
                    <div> {product.storageAddress} </div>
                </div>
                <div className="card-block__calculator">
                    <span className="calculator-title">{t('Выберите срок аренды')}</span>
                    <BlockCalc
                        categoryId={product.category.id}
                        productId={product.id}
                        productPrice={product.price}
                        buyoutTitle="Я хочу купить этот товар"
                        isBuyout={product.is_buyout as boolean}
                        variantsCreditProducts={variantsCreditProducts}
                    />
                </div>
            </div>
            <div className="action-block">
                <BasketItemActionBar productItem={product} />
                <div className="info">
                    <div>
                        <InformationOverpaymentCalculation productId={product.id} />
                    </div>

                    <div>
                        <BlockArentaInfo productId={product.id} />
                    </div>
                </div>
                <WithPopover
                    isPopover={isNewQuestionnaireAllowed && !isNewQuestionnaireAllowed.answer && isActive}
                    content={<Styled.Content>{isNewQuestionnaireAllowed?.reason}</Styled.Content>}
                >
                    <div className="btn">
                        <Button
                            variant={isActive ? 'primary' : 'neutral'}
                            stretch
                            fontSize="md"
                            onClick={handleProcessingProduct}
                            disabled={isNewQuestionnaireAllowed && !isNewQuestionnaireAllowed?.answer}
                            aria-label={isActive ? t('Перейти к оформлению') : t('Нет в наличии')}
                        >
                            {isActive ? t('Перейти к оформлению') : t('Нет в наличии')}{' '}
                            {isActive && <DoubleArrow2Icon color="#fff" />}
                        </Button>
                    </div>
                </WithPopover>
            </div>
        </Styled.Wrapper>
    );
});

BasketItemProduct.displayName = 'BasketItemProduct';
